import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Plus, Search, Globe, LayoutTemplate, Code } from 'lucide-react';
import { api } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { ContainerStatusBadge } from '@/components/StatusBadge';
import type { Site, Template, ContainerStatus } from '@/types';

export function SitesPage() {
  const { user } = useAuth();
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ContainerStatus | ''>('');
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (!user) return;

    const isAdmin = user.role === 'ADMIN';
    const tenantId = user.tenant?.id;
    if (!isAdmin && !tenantId) return;

    const url = isAdmin ? '/sites' : `/sites?tenantId=${tenantId}`;
    api.get<Site[]>(url)
      .then(setSites)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const filtered = sites.filter((s) => {
    const matchesSearch =
      !search ||
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.identifier.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || s.containerStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statuses: ContainerStatus[] = ['RUNNING', 'BUILDING', 'STOPPED', 'ERROR', 'PENDING'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-100">Sites</h1>
          <p className="mt-1 text-sm text-neutral-500">
            {sites.length} Site{sites.length !== 1 ? 's' : ''} verwalten
          </p>
        </div>
        {user?.role === 'ADMIN' && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Neue Site
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
          <input
            type="text"
            placeholder="Suchen…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-surface-400 bg-surface-200 py-2.5 pl-10 pr-4 text-sm text-neutral-200 placeholder-neutral-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ContainerStatus | '')}
          className="rounded-lg border border-surface-400 bg-surface-200 px-3 py-2.5 text-sm text-neutral-200 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="">Alle Status</option>
          {statuses.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-surface-300 bg-surface-100 py-16">
          <Globe className="mb-3 h-10 w-10 text-neutral-600" />
          <p className="text-sm text-neutral-500">Keine Sites gefunden.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-surface-300 bg-surface-100">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-surface-300 text-xs uppercase text-neutral-500">
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Identifier</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Template</th>
                <th className="px-5 py-3 font-medium">Domains</th>
                <th className="px-5 py-3 font-medium">Letztes Deploy</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-300">
              {filtered.map((site) => (
                <tr
                  key={site.id}
                  className="hover:bg-surface-200/50 transition-colors"
                >
                  <td className="px-5 py-3">
                    <Link
                      to={`/sites/${site.id}`}
                      className="font-medium text-neutral-200 hover:text-brand-400 transition-colors"
                    >
                      {site.name}
                    </Link>
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-neutral-500">
                    {site.identifier}
                  </td>
                  <td className="px-5 py-3">
                    <ContainerStatusBadge status={site.containerStatus} />
                  </td>
                  <td className="px-5 py-3 text-neutral-400">
                    {site.template?.name ?? '—'}
                  </td>
                  <td className="px-5 py-3 text-neutral-400">
                    {site.domains?.length ?? 0}
                  </td>
                  <td className="px-5 py-3 text-neutral-500 text-xs">
                    {site.lastDeployedAt
                      ? new Date(site.lastDeployedAt).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <CreateSiteModal
          tenantId={user?.tenant?.id ?? ''}
          onClose={() => setShowCreate(false)}
          onCreated={(site) => {
            setSites((prev) => [...prev, site]);
            setShowCreate(false);
          }}
        />
      )}
    </div>
  );
}

/* ─── Create Site Modal ────────────────────────────────── */

function CreateSiteModal({
  tenantId,
  onClose,
  onCreated,
}: {
  tenantId: string;
  onClose: () => void;
  onCreated: (site: Site) => void;
}) {
  const [form, setForm] = useState({
    identifier: '',
    name: '',
    repoUrl: '',
  });
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get<Template[]>('/templates')
      .then(setTemplates)
      .catch(() => {})
      .finally(() => setLoadingTemplates(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const site = await api.post<Site>('/sites', {
        identifier: form.identifier,
        name: form.name,
        tenantId,
        templateId: selectedTemplateId || undefined,
        repoUrl: !selectedTemplateId && form.repoUrl ? form.repoUrl : undefined,
      });
      onCreated(site);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Erstellen');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-surface-300 bg-surface-100 p-6 shadow-2xl">
        <h2 className="mb-4 text-lg font-semibold text-neutral-100">Neue Site erstellen</h2>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="cs-name" className="mb-1 block text-sm font-medium text-neutral-300">
              Name
            </label>
            <input
              id="cs-name"
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full rounded-lg border border-surface-400 bg-surface-200 px-3 py-2 text-sm text-neutral-200 placeholder-neutral-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="Meine Website"
            />
          </div>

          <div>
            <label htmlFor="cs-id" className="mb-1 block text-sm font-medium text-neutral-300">
              Identifier
            </label>
            <input
              id="cs-id"
              type="text"
              required
              pattern="[a-z0-9-]+"
              value={form.identifier}
              onChange={(e) => setForm((f) => ({ ...f, identifier: e.target.value }))}
              className="w-full rounded-lg border border-surface-400 bg-surface-200 px-3 py-2 text-sm text-neutral-200 placeholder-neutral-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="meine-website"
            />
            <p className="mt-1 text-xs text-neutral-500">Kleinbuchstaben, Zahlen, Bindestriche</p>
          </div>

          {/* Template Selector */}
          <div>
            <label className="mb-2 block text-sm font-medium text-neutral-300">
              Template wählen
            </label>
            {loadingTemplates ? (
              <div className="flex h-20 items-center justify-center">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {/* Custom / Kein Template */}
                <button
                  type="button"
                  onClick={() => setSelectedTemplateId(null)}
                  className={`flex flex-col items-start rounded-xl border-2 p-4 text-left transition-all ${
                    selectedTemplateId === null
                      ? 'border-brand-500 bg-brand-500/10'
                      : 'border-surface-400 bg-surface-200 hover:border-surface-500'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Code className="h-5 w-5 text-neutral-400" />
                    <span className="font-semibold text-neutral-200">Eigenes Repository</span>
                  </div>
                  <p className="mt-1 text-xs text-neutral-500">
                    Eigene Repository-URL angeben
                  </p>
                </button>

                {/* Template Cards */}
                {templates.map((tpl) => (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => setSelectedTemplateId(tpl.id)}
                    className={`flex flex-col items-start rounded-xl border-2 p-4 text-left transition-all ${
                      selectedTemplateId === tpl.id
                        ? 'border-brand-500 bg-brand-500/10'
                        : 'border-surface-400 bg-surface-200 hover:border-surface-500'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <LayoutTemplate className="h-5 w-5 text-brand-400" />
                      <span className="font-semibold text-neutral-200">{tpl.name}</span>
                    </div>
                    {tpl.description && (
                      <p className="mt-1 text-xs text-neutral-500">{tpl.description}</p>
                    )}
                    {tpl.features.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {tpl.features.map((f) => (
                          <span
                            key={f}
                            className="rounded-full bg-surface-300 px-2 py-0.5 text-[11px] font-medium text-neutral-400"
                          >
                            {f}
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Repo URL — only when no template selected */}
          {selectedTemplateId === null && (
            <div>
              <label htmlFor="cs-repo" className="mb-1 block text-sm font-medium text-neutral-300">
                Repository URL <span className="text-neutral-500">(optional)</span>
              </label>
              <input
                id="cs-repo"
                type="url"
                value={form.repoUrl}
                onChange={(e) => setForm((f) => ({ ...f, repoUrl: e.target.value }))}
                className="w-full rounded-lg border border-surface-400 bg-surface-200 px-3 py-2 text-sm text-neutral-200 placeholder-neutral-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
                placeholder="https://github.com/..."
              />
            </div>
          )}

          {error && (
            <p className="text-sm text-red-400">{error}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm text-neutral-400 hover:text-neutral-200 transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Erstelle…' : 'Erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
